import { ChevronLeft, ChevronRight } from "lucide-react";
import { useRef } from "react";
import type { ReactNode } from "react";

import { MealScan } from "@/features/portal/components/meal-scan";
import { AndroidGlyph, AppleGlyph } from "@/features/portal/components/platform-glyphs";
import { Accent, Eyebrow, Monogram, Section, sectionTitle } from "@/features/portal/components/portal-ui";
import { photos } from "@/features/portal/content/photos";
import { upcomingDays } from "@/features/portal/utils/upcoming-days";
import { BrandMark } from "@/features/site/components/brand-mark";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

const orb =
  "grid size-[46px] place-items-center rounded-full outline-none transition-[background-color,transform] focus-visible:ring-2 focus-visible:ring-ring active:scale-[0.96]";

const panel =
  "w-4/5 rounded-[18px] bg-background p-3.5 text-[13px] shadow-[0_20px_40px_-16px_color-mix(in_oklch,var(--foreground)_30%,transparent)]";

const BookingDrawing = () => (
  <div className={panel}>
    <div className="flex items-center gap-2.5">
      <Monogram name={m.portal_step_find_card_title()} className="size-[34px] rounded-[11px] text-[11px]" />
      <div>
        <b className="block font-semibold">{m.portal_step_remind_card_title()}</b>
        <span className="text-muted-foreground">{m.portal_sample_session()}</span>
      </div>
    </div>
    <div className="mt-3 grid grid-cols-3 gap-1.5">
      {["07:00", "17:30", "18:30"].map((time, index) => (
        <span
          key={time}
          className={cn(
            "grid h-8 place-items-center rounded-[10px] bg-muted text-xs font-semibold tabular-nums",
            index === 1 && "bg-foreground text-background",
          )}
        >
          {time}
        </span>
      ))}
    </div>
  </div>
);

const WeekDrawing = () => {
  const [first, second, third] = upcomingDays(3);
  const entries = [
    {
      id: "coach",
      day: first,
      title: m.portal_sample_session(),
      detail: "18:00",
      tone: "bg-primary text-primary-foreground",
    },
    {
      id: "plan",
      day: second,
      title: m.portal_step_train_card_title(),
      detail: m.portal_step_train_card_detail(),
      tone: "bg-primary/15 text-[var(--site-ink)]",
    },
    {
      id: "rest",
      day: third,
      title: m.portal_sample_rest(),
      detail: "",
      tone: "bg-[var(--site-well)] text-muted-foreground",
    },
  ];
  return (
    <div className={cn(panel, "grid gap-1.5")}>
      {entries.map((entry) => (
        <span
          key={entry.id}
          className={cn("rounded-[10px] px-2.5 py-1.5 text-xs leading-tight font-semibold", entry.tone)}
        >
          {entry.day?.short} · {entry.title}
          {entry.detail ? <small className="block font-medium opacity-75">{entry.detail}</small> : null}
        </span>
      ))}
    </div>
  );
};

/** One notification on a lock screen, from the app. */
const Notice = ({ title, detail }: { title: string; detail: string }) => (
  <span className="flex w-full min-w-0 items-center gap-2 rounded-xl bg-[var(--site-well)] p-1.5 pr-2 text-left">
    <BrandMark className="size-6 shrink-0 rounded-md [&_svg]:h-3" />
    <span className="min-w-0 leading-tight">
      <b className="block truncate text-[11px] font-semibold">{title}</b>
      <small className="block truncate text-[10px] text-muted-foreground">{detail}</small>
    </span>
  </span>
);

/**
 * Two phones rising out of the card, an iPhone with its notch in front and an Android with its
 * punch-hole camera behind, the app's notifications on the front one's lock screen.
 */
const PhonesDrawing = () => (
  <div className="absolute inset-0">
    <div className="absolute top-4 left-4 z-10 flex gap-1.5">
      <span className="grid size-10 place-items-center rounded-full bg-foreground text-background">
        <AppleGlyph className="size-[18px]" />
      </span>
      <span className="grid size-10 place-items-center rounded-full bg-primary text-primary-foreground">
        <AndroidGlyph className="size-5" />
      </span>
    </div>
    <div className="absolute bottom-[-40px] left-[14%] h-[210px] w-[130px] -rotate-[10deg] rounded-[28px] border-[5px] border-foreground/70 bg-[var(--site-wash)] shadow-[0_20px_40px_-16px_rgb(0_0_0/0.5)]">
      <span className="absolute top-2.5 left-1/2 size-2.5 -translate-x-1/2 rounded-full bg-foreground/70" />
      <AndroidGlyph className="absolute top-12 left-1/2 size-14 -translate-x-1/2 text-primary" />
      <span className="absolute inset-x-4 top-[116px] h-2 rounded-full bg-primary/40" />
      <span className="absolute top-[132px] left-4 h-2 w-1/2 rounded-full bg-primary/25" />
    </div>
    <div className="absolute right-[12%] bottom-[-56px] h-[250px] w-[160px] rotate-[4deg] rounded-[32px] border-[6px] border-foreground bg-background px-2.5 pt-7 shadow-[0_30px_60px_-20px_rgb(0_0_0/0.6)]">
      <span className="absolute top-1.5 left-1/2 h-4 w-14 -translate-x-1/2 rounded-full bg-foreground" />
      <b className="block text-center text-[34px] leading-none font-medium tracking-[-0.05em] tabular-nums">18:00</b>
      <div className="mt-3 grid grid-cols-[minmax(0,1fr)] gap-1.5">
        <Notice title={m.portal_step_remind_card_title()} detail={m.portal_step_remind_card_detail()} />
        <Notice title={m.portal_rail_plan_title()} detail={m.portal_step_train_card_title()} />
      </div>
    </div>
  </div>
);

const Photo = ({ src }: { src: string }) => (
  <img src={src} alt="" loading="lazy" className="absolute inset-0 size-full object-cover grayscale" />
);

const things = (): { id: string; label: string; title: string; body: string; art: ReactNode }[] => [
  {
    id: "book",
    label: "01",
    title: m.portal_rail_book_title(),
    body: m.portal_rail_book_body(),
    art: <BookingDrawing />,
  },
  {
    id: "plan",
    label: "02",
    title: m.portal_rail_plan_title(),
    body: m.portal_rail_plan_body(),
    art: <Photo src={photos.deadliftPlate} />,
  },
  {
    id: "scan",
    label: `03 · ${m.portal_badge_new()}`,
    title: m.portal_rail_scan_title(),
    body: m.portal_rail_scan_body(),
    art: <MealScan className="absolute inset-0 rounded-none" />,
  },
  { id: "week", label: "04", title: m.portal_rail_week_title(), body: m.portal_rail_week_body(), art: <WeekDrawing /> },
  {
    id: "app",
    label: "05",
    title: m.portal_rail_app_title(),
    body: m.portal_rail_app_body(),
    art: <PhonesDrawing />,
  },
];

/**
 * Everything the app holds, as one row of cards to slide through: five things, one account. Each
 * card is a picture of the thing and two lines about it, so the section stays a glance, not a read.
 */
export const FeaturesRail = () => {
  const rail = useRef<HTMLUListElement>(null);
  const slide = (direction: 1 | -1) => {
    const card = rail.current?.firstElementChild;
    rail.current?.scrollBy({ left: direction * ((card?.clientWidth ?? 380) + 12), behavior: "smooth" });
  };

  return (
    <Section id="features">
      <div className="mb-9 flex items-end justify-between gap-6 px-1">
        <div>
          <Eyebrow>{m.portal_rail_eyebrow()}</Eyebrow>
          <h2 className={cn(sectionTitle, "mt-4")}>
            {m.portal_rail_title_lead()} <Accent>{m.portal_rail_title_tail()}</Accent>
          </h2>
        </div>
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            aria-label={m.portal_rail_previous()}
            className={cn(orb, "bg-card hover:bg-muted")}
            onClick={() => slide(-1)}
          >
            <ChevronLeft aria-hidden className="size-[18px]" />
          </button>
          <button
            type="button"
            aria-label={m.portal_rail_next()}
            className={cn(orb, "bg-primary text-primary-foreground hover:brightness-105")}
            onClick={() => slide(1)}
          >
            <ChevronRight aria-hidden className="size-[18px]" />
          </button>
        </div>
      </div>
      <ul
        ref={rail}
        className="grid snap-x snap-mandatory auto-cols-[minmax(280px,380px)] grid-flow-col gap-3 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {things().map((thing) => (
          <li key={thing.id} className="flex snap-start flex-col overflow-hidden rounded-[28px] bg-card">
            <div
              aria-hidden
              className="relative grid h-[260px] place-items-center overflow-hidden bg-[var(--site-tint)]"
            >
              {thing.art}
            </div>
            <div className="px-6 pt-5 pb-6">
              <span className="text-xs font-semibold tracking-[0.14em] text-[var(--site-ink)] uppercase">
                {thing.label}
              </span>
              <h3 className="mt-2.5 text-2xl font-medium tracking-[-0.03em]">{thing.title}</h3>
              <p className="mt-2 text-[15px] leading-normal text-muted-foreground">{thing.body}</p>
            </div>
          </li>
        ))}
      </ul>
    </Section>
  );
};
